import React, { useState, FC, useContext } from 'react'
import { truncate } from 'lodash'
import { Column } from 'react-table'
import { t } from '@lingui/macro'
import {
  SqlOrderDirectionEnum,
  UserActivityOverviewOrderColumn
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'
import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { getPagesCount } from '../utils'
import { IBaseOverviewFilter } from '../interfaces'
import { DownloadUserActivityCSV } from './DownloadUserActivityCSV'
import { useActivityOverviewTableQuery } from './ActivityOverviewTable.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'

export interface IOverviewFilter extends IBaseOverviewFilter {
  orderColumn: UserActivityOverviewOrderColumn
  userId: number
}

const ActivityOverviewTable: FC<{
  userId: number
}> = ({ userId }) => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewFilter, setOverviewFilters] = useState<IOverviewFilter>({
    limit: 20,
    orderColumn: UserActivityOverviewOrderColumn.viewsCount,
    orderDirection: SqlOrderDirectionEnum.DESC,
    userId
  })

  const overviewTableQuery = {
    ...overviewFilter,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useActivityOverviewTableQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      overviewFilter: overviewTableQuery
    }
  })

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Resource`,
      id: UserActivityOverviewOrderColumn.resourceName,
      accessor: UserActivityOverviewOrderColumn.resourceName,
      Cell: (row) => {
        return (
          <>
            <strong className="dark">
              {truncate(row.value, { length: 80 })}
            </strong>
            {row.original.is_archived && <StatusArchivedGrey />}
          </>
        )
      }
    },
    {
      Header: t`Views`,
      className: 'score',
      accessor: UserActivityOverviewOrderColumn.viewsCount,
      maxWidth: 120,
      Cell: (row) => <span className="green">{row.value}</span>
    },
    {
      Header: t`Last activity`,
      maxWidth: 140,
      accessor: UserActivityOverviewOrderColumn.lastViewedAt,
      Cell: (row) => formatDateShort(row.value)
    }
  ]

  let pagesCount = 1
  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.resources.userActivityOverviewCount,
      overviewTableQuery.limit
    )
  }

  return (
    <div>
      <TableExtendedOld
        loading={loading}
        manual
        data={data && data.analytics.resources.userActivityOverview}
        tableHeader={t`Activity`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={overviewTableQuery.limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={1}
        pages={pagesCount}
        filterFields={[UserActivityOverviewOrderColumn.resourceName]}
        getTrProps={(_state, row) => ({
          onClick: () =>
            navigateTo(
              linkBuilder.analyticsResourceDetail(row.original.resourceId),
              true
            ),
          className: `clickable ${
            row && row.original.is_archived ? 'archived' : ''
          }`,
          ...DEFAULT_TR_PROPS
        })}
        paginationContentAfter={
          data &&
          data.analytics && (
            <DownloadUserActivityCSV
              userActivityOverviewFilter={overviewTableQuery}
              userActivityCount={
                data.analytics.resources.userActivityOverviewCount
              }
            />
          )
        }
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setOverviewFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as UserActivityOverviewOrderColumn)
                : UserActivityOverviewOrderColumn.viewsCount,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />
    </div>
  )
}

export default ActivityOverviewTable
